import { Ad, adFromMap } from "./AdModel";
import { AdsConfig, DEFAULT_ADS_CONFIG, parseAdsConfig } from "./AdsConfig";

/**
 * Partitioned shape returned by the `getAds` callable (see
 * `@highnote/server/src/functions/handlers/ads/getAds.ts`).
 *
 *   - `carouselAds`: ads with `adType !== 'spacePinned'` (sponsored +
 *     brand partner), sorted by priority.
 *   - `pinnedAds`: ads with `adType === 'spacePinned'`, already capped
 *     by `config.spacePinnedMax` on the server.
 *   - `config`: the merged AdsConfig — kill switches + cadence knobs.
 */
export type AdsResponse = {
  carouselAds: Ad[];
  pinnedAds: Ad[];
  config: AdsConfig;
};

const EMPTY_RESPONSE: AdsResponse = {
  carouselAds: [],
  pinnedAds: [],
  config: { ...DEFAULT_ADS_CONFIG },
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

const normalizeAds = (raw: unknown): Ad[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((item): item is Record<string, unknown> =>
      Boolean(item && typeof item === "object" && !Array.isArray(item)),
    )
    .map(adFromMap);
};

/**
 * Safely normalizes an unknown payload from the `getAds` callable into
 * an [AdsResponse]. Any shape mismatch — missing keys, wrong types,
 * null — yields empty ad lists and the default config, so the client
 * can always render without guarding.
 *
 * Back-compat: if the payload still carries the legacy `{ ads: [] }`
 * single-list shape (which no deployed server version emits today, but
 * may appear during a staged rollout), we route those ads into the
 * carousel partition so the UI still sees them.
 */
export const parseAdsResponse = (input: unknown): AdsResponse => {
  if (!isRecord(input)) return { ...EMPTY_RESPONSE };

  const config = parseAdsConfig(input.config);

  // Preferred shape: partitioned lists.
  if (Array.isArray(input.carouselAds) || Array.isArray(input.pinnedAds)) {
    return {
      carouselAds: normalizeAds(input.carouselAds),
      pinnedAds: normalizeAds(input.pinnedAds),
      config,
    };
  }

  // Legacy shape: flat `ads` list. Kept for safety during rollout;
  // a server returning the legacy shape is a bug on that deployment,
  // but we avoid blanking the UI in that case.
  if (Array.isArray(input.ads)) {
    return {
      carouselAds: normalizeAds(input.ads),
      pinnedAds: [],
      config,
    };
  }

  return { carouselAds: [], pinnedAds: [], config };
};
